import {
  ArrowRight,
  BookmarkCheck,
  LifeBuoy,
  LoaderCircle,
  OctagonAlert,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { Fragment, useRef, type ReactNode, useEffect } from "react";
import { Link } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaCheckbox } from "@/shared/components/forms/ColocaCheckbox";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { useAmountFormatter } from "../../hooks/useAmountFormatter";
import type { CryptoSwapSubmitPhase } from "../../hooks/useCryptoSwapForm";
import { useOtcTiming } from "../../hooks/useOtcTiming";
import type { OtcPair, OtcQuote } from "../../types/cryptoSwaps.types";
import { chunkAddress } from "../../utils/cryptoSwapAddress";
import type { CryptoSwapErrorKind } from "../../utils/cryptoSwapErrors";
import { slotMarker, splitTemplate } from "../../utils/cryptoSwapFormat";
import { CRYPTO_SWAPS_HISTORY_PATH } from "../../utils/cryptoSwapLinks";
import type { CryptoSwapBlock } from "../../utils/cryptoSwapStatus";
import { AssetAvatar } from "../shared/CryptoSwapBits";
import { FeesValue } from "../shared/FeesValue";
import { NetworkIcon } from "../shared/NetworkIcon";
import { EstimateLine } from "../shared/OperationEta";
import { BuyTransit } from "../shared/OtcTransit";
import { QuoteTimerBar } from "./QuoteTimerBar";

export interface ConfirmSwapDialogProps {
  open: boolean;
  onClose: () => void;
  pair: OtcPair | null;
  quote: OtcQuote | null;
  isPricing: boolean;
  address: string;
  /** Which saved wallet the address is, or the alias it will be saved under. */
  walletNote?: string | null;
  networkLabel: string;
  /** The network code, for its mark next to the label. */
  network?: string;
  concept: string;
  clockOffsetMs: number;
  /** The form's review block: anything but null means the price on screen cannot be bought right now. */
  block: CryptoSwapBlock;
  blockMessage: string | null;
  quoteErrorMessage: string | null;
  acknowledged: boolean;
  onAcknowledgedChange: (acknowledged: boolean) => void;
  phase: CryptoSwapSubmitPhase;
  errorKind: CryptoSwapErrorKind | null;
  errorMessage: string | null;
  onConfirm: () => void;
  onRequote: () => void;
}

/** Last stop before an irreversible transfer: the address in chunks, acknowledged as compared. */
export function ConfirmSwapDialog({
  open,
  onClose,
  pair,
  quote,
  isPricing,
  address,
  networkLabel,
  network,
  walletNote,
  concept,
  clockOffsetMs,
  block,
  blockMessage,
  quoteErrorMessage,
  acknowledged,
  onAcknowledgedChange,
  phase,
  errorKind,
  errorMessage,
  onConfirm,
  onRequote,
}: ConfirmSwapDialogProps) {
  const { t } = useTranslations("cryptoSwaps");
  const timing = useOtcTiming();
  const headlineRef = useRef<HTMLParagraphElement>(null);

  // Whatever held focus may have unmounted with the price; keep it inside the dialog.
  useEffect(() => {
    if (open && !quote && document.activeElement === document.body) headlineRef.current?.focus();
  }, [open, quote]);
  const format = useAmountFormatter(pair);
  const asset = quote?.receive.currency ?? pair?.receive_currency ?? "";

  // The order may already exist: a retry is not the honest exit, the history is.
  const isAmbiguous = errorKind === "unconfirmed" || errorKind === "conflict";
  const pricing = isPricing || block === "pricing" || block === "stale";
  const needsPrice = !pricing && (block === "expired" || block === "unpriced" || block === "amount");
  const otherBlock = block !== null && !pricing && !needsPrice;

  const headline = quote
    ? splitTemplate(t("confirm_headline", { receive: slotMarker("receive"), send: slotMarker("send") }))
    : [];
  const slot = (name: string): ReactNode =>
    !quote ? null : name === "receive" ? (
      <span className="inline-flex items-baseline gap-1.5 font-mono whitespace-nowrap tabular-nums">
        <AssetAvatar asset={quote.receive.currency} kind="ONCHAIN" size="sm" className="self-center" />
        {`${format(quote.receive.amount, quote.receive.currency)} ${quote.receive.currency}`}
      </span>
    ) : (
      <span className="font-mono whitespace-nowrap tabular-nums">
        {`${format(quote.send.amount, quote.send.currency)} ${quote.send.currency}`}
      </span>
    );

  const primary = needsPrice ? (
    <ColocaButton variant="primary" icon={RefreshCw} onClick={onRequote}>
      {t("confirm_refresh")}
    </ColocaButton>
  ) : (
    <ColocaButton
      variant="primary"
      iconTrailing={ArrowRight}
      loading={pricing}
      disabled={block !== null || !acknowledged || !address}
      onClick={onConfirm}
    >
      {t("confirm_create", { asset })}
    </ColocaButton>
  );

  return (
    <ColocaDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t("confirm_title")}
      mobileFullscreen
      initialFocus={headlineRef}
      toolbar={
        phase === "creating" || (phase === "failed" && isAmbiguous) ? undefined : (
          <div className="dark rounded-xl border border-border bg-card p-3 text-card-foreground">
            <QuoteTimerBar
              key={quote?.id ?? "none"}
              label={t("confirm_price")}
              quote={quote}
              isPricing={pricing}
              clockOffsetMs={clockOffsetMs}
              announce={open}
            />
          </div>
        )
      }
      footer={
        phase === "creating" ? null : phase === "failed" && isAmbiguous ? (
          <ColocaButton variant="primary" icon={LifeBuoy} render={<Link to={CRYPTO_SWAPS_HISTORY_PATH} />}>
            {t("error_unconfirmed_history")}
          </ColocaButton>
        ) : (
          <>
            <ColocaButton variant="ghost" onClick={onClose}>
              {t("confirm_back")}
            </ColocaButton>
            {primary}
          </>
        )
      }
    >
      {phase === "creating" ? (
        <div role="status" className="flex flex-col items-center gap-3 py-8 text-center">
          {network ? (
            <BuyTransit
              className="mb-3 max-w-md"
              sendCurrency={quote?.send.currency ?? pair?.send_currency ?? ""}
              receiveCurrency={asset}
              network={network}
              address={address}
            />
          ) : (
            <LoaderCircle aria-hidden="true" className="size-8 animate-spin text-primary" />
          )}
          <p className="text-base font-semibold text-foreground">{t("order_creating_title")}</p>
          <p className="text-sm text-muted-foreground">{t("order_creating_hint")}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {phase === "failed" && errorMessage && (
            <ColocaAlert variant={isAmbiguous ? "warning" : "destructive"} text={errorMessage} />
          )}

          {/* Mounted even between prices: it holds the initial focus, and unmounting it drops focus to the page. */}
          <p
            ref={headlineRef}
            tabIndex={-1}
            className={cn(
              "text-2xl leading-snug font-semibold tracking-tight text-foreground transition-opacity outline-none",
              (pricing || needsPrice) && "opacity-50",
              !quote && "sr-only",
            )}
          >
            {headline.map((part, index) =>
              part.kind === "text" ? (
                <Fragment key={index}>{part.value}</Fragment>
              ) : (
                <Fragment key={index}>{slot(part.name)}</Fragment>
              ),
            )}
          </p>

          {needsPrice && (
            <ColocaAlert
              variant="warning"
              heading={block === "expired" ? t("confirm_expired_title") : undefined}
              text={block === "expired" ? t("confirm_expired_message") : (quoteErrorMessage ?? blockMessage)}
            />
          )}
          {otherBlock && blockMessage && <ColocaAlert variant="warning" text={blockMessage} />}

          {quote && (
            <dl
              className={cn(
                "m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-3 text-sm transition-opacity",
                (pricing || needsPrice) && "opacity-50",
              )}
            >
              <Row label={t("confirm_price")} mono>
                {t("price_line", {
                  asset: quote.receive.currency,
                  price: format(quote.price.value, quote.price.currency),
                  currency: quote.price.currency,
                })}
              </Row>
              <Row label={t("confirm_fees")}>
                <FeesValue fees={quote.fees} pair={pair} />
              </Row>
              <Row label={t("confirm_network")}>
                <span className="inline-flex items-center gap-1.5">
                  {network && <NetworkIcon network={network} size={16} />}
                  {networkLabel}
                </span>
              </Row>
              {network && timing?.buy[network] && (
                <Row label={t("eta_label")}>
                  <EstimateLine estimate={timing.buy[network]} />
                </Row>
              )}
              {concept && <Row label={t("confirm_concept")}>{concept}</Row>}
            </dl>
          )}

          {address && <AddressReview address={address} />}
          {address && walletNote && (
            <p className="-mt-2 flex items-center gap-2 text-[13px] text-muted-foreground">
              <BookmarkCheck aria-hidden="true" className="size-4 shrink-0 text-primary" />
              {walletNote}
            </p>
          )}

          <ColocaAlert
            variant="destructive"
            icon={OctagonAlert}
            heading={t("confirm_network_warning_title", { network: networkLabel })}
            text={t("confirm_network_warning", { network: networkLabel })}
          />

          <div
            className={cn(
              "rounded-xl border p-4 transition-colors",
              acknowledged ? "border-primary bg-primary/5" : "border-border bg-card",
            )}
          >
            <ColocaCheckbox
              checked={acknowledged}
              onCheckedChange={onAcknowledgedChange}
              label={
                <span className="leading-snug">{t("confirm_acknowledge", { network: networkLabel })}</span>
              }
            />
          </div>
        </div>
      )}
    </ColocaDialog>
  );
}

function Row({ label, mono, children }: { label: string; mono?: boolean; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={cn("m-0 text-right break-words", mono && "font-mono tabular-nums")}>{children}</dd>
    </>
  );
}

/** Chunked for a human comparing against a wallet; the whole address is a sibling for assistive tech. */
export function AddressReview({ address, title }: { address: string; title?: string }) {
  const { t } = useTranslations("cryptoSwaps");
  const { head, middle, tail } = chunkAddress(address);

  return (
    <section className="flex flex-col gap-3 rounded-xl border-2 border-foreground/80 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-semibold tracking-[0.08em] text-muted-foreground uppercase">
          {title ?? t("confirm_destination_title")}
        </span>
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-success-container-foreground">
          <ShieldCheck aria-hidden="true" className="size-3.5" />
          {t("confirm_checksum_ok")}
        </span>
      </div>
      <span className="sr-only">{address}</span>
      <p aria-hidden="true" className="flex flex-wrap gap-x-3 gap-y-1 font-mono text-lg">
        <span className="rounded bg-primary/10 px-1.5 font-semibold text-foreground">{head}</span>
        {middle.map((group, index) => (
          <span key={`${group}-${index}`} className="text-muted-foreground">
            {group}
          </span>
        ))}
        <span className="rounded bg-primary/10 px-1.5 font-semibold text-foreground">{tail}</span>
      </p>
      <p className="text-[13px] leading-relaxed text-muted-foreground">{t("destination_compare")}</p>
    </section>
  );
}
